import app from 'flarum/forum/app';
import Button from 'flarum/common/components/Button';
import Stream from 'flarum/common/utils/Stream';
import extractText from 'flarum/common/utils/extractText';
import passwordless from '../../common/passwordless';
import CodeInput from '../components/CodeInput';
export { passwordless };

/** O estado do passo do código, guardado na instância do modal. */

/** Espera depois de pedir um código, antes de poder pedir outro. */
const RESEND_DELAY = 30_000;
export function codeLength() {
  return app.forum.attribute('magicLinkCodeLength') || 6;
}
export function createState() {
  return {
    ref: null,
    email: '',
    code: Stream(''),
    honeypot: Stream(''),
    resendAt: 0
  };
}
export function requestCode(state, body, errorHandler) {
  return app.request({
    method: 'POST',
    url: app.forum.attribute('apiUrl') + '/magic-link',
    body: {
      ...body,
      honeypot: state.honeypot()
    },
    errorHandler
  }).then(response => {
    state.ref = response.ref;
    state.code('');
    state.resendAt = Date.now() + RESEND_DELAY;
  });
}

/**
 * fof/recaptcha: o estado do captcha que ela põe no próprio modal, quando a
 * opção dela vale para este tipo de pedido.
 */
export function recaptchaFor(modal, type) {
  if (!modal.recaptcha || modal.recaptchaDisable) return null;
  if (!app.forum.attribute('fof-recaptcha.configured') || !app.forum.attribute(`fof-recaptcha.${type}`)) return null;
  return modal.recaptcha;
}

/** O checkbox precisa da pessoa; o invisível e o v3 geram o token sozinhos. */
export function recaptchaIsCheckbox(modal, type) {
  const recaptcha = recaptchaFor(modal, type);
  return !!recaptcha && !recaptcha.requiresAsyncToken();
}
export async function recaptchaParams(modal, type) {
  const recaptcha = recaptchaFor(modal, type);
  if (!recaptcha) return {};
  return {
    'g-recaptcha-response': await recaptchaToken(recaptcha),
    'g-recaptcha-action': recaptcha.action
  };
}

/**
 * No modo invisível, o acquireToken() da fof/recaptcha 2.0.1 não termina: o
 * widget guarda o callback do momento em que foi desenhado. Aqui o token vem do
 * getResponse() depois do execute(), sem depender do callback.
 */
export function recaptchaToken(recaptcha) {
  if (!recaptcha.isInvisible()) {
    return recaptcha.requiresAsyncToken() ? recaptcha.acquireToken() : Promise.resolve(recaptcha.getResponse());
  }
  const grecaptcha = window.grecaptcha;
  const widgetId = recaptcha.widgetId ?? undefined;
  grecaptcha.reset(widgetId);
  grecaptcha.execute(widgetId);

  // Até 2 minutos: pode aparecer um desafio de imagens para resolver.
  return new Promise((resolve, reject) => {
    const started = Date.now();
    const poll = () => {
      const token = grecaptcha.getResponse(widgetId);
      if (token) resolve(token);else if (Date.now() - started > 120_000) reject(new Error('recaptcha timeout'));else setTimeout(poll, 250);
    };
    poll();
  });
}

/** O token vale uma vez: depois de usado, o checkbox precisa ser marcado de novo. */
export function recaptchaReset(modal, type) {
  try {
    recaptchaFor(modal, type)?.reset();
  } catch {
    // O widget pode já ter saído da tela.
  }
}
export function recaptchaErrorAlert() {
  return {
    type: 'error',
    content: app.translator.trans('fof-recaptcha.lib.error')
  };
}
export function verifyCode(state, errorHandler) {
  return app.request({
    method: 'POST',
    url: app.forum.attribute('baseUrl') + '/magic-link/verify',
    body: {
      ref: state.ref,
      code: state.code()
    },
    errorHandler
  });
}

/** Bilhete que o servidor aceita no lugar da senha atual (trocar e-mail, apagar a conta). */
export function reauthTicket(state) {
  return `magic-link:${state.ref}:${state.code().replace(/\D/g, '')}`;
}

/**
 * Campo que só robô preenche. Fica fora da tela, não escondido com display,
 * porque alguns robôs pulam campos invisíveis.
 */
export function honeypotField(state) {
  return m("div", {
    className: "MagicLink-honeypot",
    "aria-hidden": "true"
  }, m("input", {
    type: "text",
    name: "magicLinkWebsite",
    tabindex: -1,
    autocomplete: "off",
    bidi: state.honeypot
  }));
}

/**
 * O passo do código: título, texto, as caixas dos dígitos e os links de
 * reenviar e trocar o e-mail. As classes são as de formulário do core, então
 * o tema (Avocado, Canela) estiliza como o resto do login.
 */
export function addCodeFields(items, state, options) {
  const seconds = Math.ceil((state.resendAt - Date.now()) / 1000);

  // A contagem do "Reenviar código (29s)" redesenha a cada segundo até zerar.
  if (seconds > 0) setTimeout(() => m.redraw(), 1000);

  // dir="auto": num fórum RTL sem tradução da extensão, o texto em inglês fica
  // com a pontuação no lugar.
  items.add('magicLinkHeading', m("h3", {
    className: "MagicLink-heading",
    dir: "auto"
  }, app.translator.trans('ramon-magic-link.forum.code.heading')), 50);
  items.add('magicLinkHint', m("p", {
    className: "helpText MagicLink-hint",
    dir: "auto"
  }, state.email.includes('@') ? app.translator.trans('ramon-magic-link.forum.code.sent_text', {
    length: codeLength(),
    email: m("strong", null, state.email)
  }) : app.translator.trans('ramon-magic-link.forum.code.sent_text_username', {
    length: codeLength(),
    username: m("strong", null, state.email)
  })), 40);
  items.add('magicLinkCode', m("div", {
    className: "Form-group MagicLink-codeGroup"
  }, m(CodeInput, {
    code: state.code,
    length: codeLength(),
    label: extractText(app.translator.trans('ramon-magic-link.forum.code.label')),
    disabled: options.loading
  })), 30);
  items.add('magicLinkActions', m("div", {
    className: "Form-group MagicLink-actions"
  }, m(Button, {
    className: "Button Button--text Button--link",
    type: "button",
    onclick: options.onResend,
    disabled: options.loading || seconds > 0
  }, seconds > 0 ? app.translator.trans('ramon-magic-link.forum.code.resend_countdown', {
    seconds
  }) : app.translator.trans('ramon-magic-link.forum.code.resend_button')), options.onChangeEmail && m(Button, {
    className: "Button Button--text Button--link",
    type: "button",
    onclick: options.onChangeEmail,
    disabled: options.loading
  }, app.translator.trans('ramon-magic-link.forum.code.change_email_link'))), -20);
}
export function submitButton(key, loading) {
  return m("div", {
    className: "Form-group"
  }, m(Button, {
    className: "Button Button--primary Button--block",
    type: "submit",
    loading: loading
  }, app.translator.trans(key)));
}
export function resentAlert() {
  return {
    type: 'success',
    content: app.translator.trans('ramon-magic-link.forum.code.resent_message')
  };
}
flarum.reg.add('ramon-magic-link', 'forum/utils/magicLink', { passwordless: passwordless,codeLength: codeLength,createState: createState,requestCode: requestCode,recaptchaFor: recaptchaFor,recaptchaIsCheckbox: recaptchaIsCheckbox,recaptchaToken: recaptchaToken,recaptchaReset: recaptchaReset,recaptchaErrorAlert: recaptchaErrorAlert,verifyCode: verifyCode,reauthTicket: reauthTicket,honeypotField: honeypotField,addCodeFields: addCodeFields,submitButton: submitButton,resentAlert: resentAlert, });